import { DistributionChannel, TemplateInputModel, TemplateViewModel } from '@ail/notifications-api'
import { useEffect, useMemo, useState } from 'react'
import useCopy from '../../../hooks/useCopy'
import IdIsCopied from '../../../helpers/IdIsCopied'
import {
  useDeleteTemplateMutation,
  usePostTemplateMutation,
  usePutTemplateMutation,
} from '../../../redux/api/notificationsApi'
import Confirm from '../../common/Confirm/Confirm'
import TemplateHtmlEditor from './TemplateHtmlEditor'
import { useTranslation } from 'react-i18next'

interface Props {
  template?: TemplateViewModel
  draftMeta?: { name: string; channel: DistributionChannel; language: string }
  onDirtyChange?: (isDirty: boolean) => void
  onSaved?: () => void
}

const normalize = (str: string | undefined) => str?.replace(/\r\n/g, '\n') ?? ''

export default function TemplateEditor({ template, draftMeta, onDirtyChange, onSaved }: Props) {
  const { t } = useTranslation('common')
  const [subject, setSubject] = useState(template?.subject ?? '')
  const [body, setBody] = useState(template?.body ?? '')
  const [isEditing, setIsEditing] = useState(!template)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [htmlEditorOpen, setHtmlEditorOpen] = useState(false)
  const { copy, isCopied } = useCopy()
  const [postTemplate, postState] = usePostTemplateMutation()
  const [putTemplate, putState] = usePutTemplateMutation()
  const [deleteTemplate] = useDeleteTemplateMutation()
  const isSaving = postState.isLoading || putState.isLoading
  useEffect(() => {
    setSubject(template?.subject ?? '')
    setBody(template?.body ?? '')
    setIsEditing(!template)
  }, [template?.id, template?.subject, template?.body, template])

  const isDirty = useMemo(
    () => normalize(subject) !== normalize(template?.subject) || normalize(body) !== normalize(template?.body),
    [subject, body, template?.subject, template?.body]
  )

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const buildInputModel = (meta: {
    name: string
    channel: DistributionChannel
    language: string
  }): TemplateInputModel => ({
    name: meta.name,
    channel: meta.channel,
    language: meta.language,
    subject,
    body,
  })

  const submit = async () => {
    if (isSaving) return
    try {
      if (template) {
        await putTemplate({ templateId: template.id, model: buildInputModel(template) }).unwrap()
      } else if (draftMeta) {
        await postTemplate(buildInputModel(draftMeta)).unwrap()
      } else {
        return
      }
      setIsEditing(false)
      onSaved?.()
    } catch (err) {
      console.error('TemplateEditor submit failed', err)
    }
  }

  const onPrimaryClick = () => (isEditing ? submit() : setIsEditing(true))

  const handleDeleteConfirm = async (confirm: boolean) => {
    setConfirmDelete(false)
    if (!confirm || !template) return
    try {
      await deleteTemplate(template.id).unwrap()
      onSaved?.()
    } catch (err) {
      console.error(`Template ${template.id} delete failed`, err)
    }
  }

  return (
    <div className="flex notEditor">
      <div className="flex">
        <div className="editor">
          <label htmlFor={`subject_${template?.id ?? 'new'}`}>Subject</label>
          <textarea
            id={`subject_${template?.id ?? 'new'}`}
            placeholder="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            rows={3}
            disabled={!isEditing}
          />
        </div>
        <div className="editor">
          <label htmlFor={`body_${template?.id ?? 'new'}`}>
            Body
            <i className="icon-code" onClick={() => setHtmlEditorOpen(true)} />
          </label>
          <textarea
            id={`body_${template?.id ?? 'new'}`}
            placeholder="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            rows={3}
            disabled={!isEditing}
          />
        </div>
      </div>
      <div className="actionsBox">
        {template && (
          <div>
            <i className="icon-id" onClick={() => copy(template.id)}></i>
          </div>
        )}
        <div className="actions">
          <i className={isEditing ? 'icon-save' : 'icon-edit'} onClick={onPrimaryClick} />
          {isEditing && <i className="icon-close" onClick={() => setIsEditing(false)} />}
          {template?.id && <i className="icon-clear" onClick={() => setConfirmDelete(true)} />}
        </div>
      </div>
      {htmlEditorOpen && (
        <TemplateHtmlEditor
          visible={htmlEditorOpen}
          templateName={template?.name ?? draftMeta?.name ?? ''}
          subject={subject}
          body={body}
          onClose={() => setHtmlEditorOpen(false)}
          onConfirm={({ subject: newSubject, body: newBody }) => {
            setSubject(newSubject)
            setBody(newBody)
            setIsEditing(true)
            setHtmlEditorOpen(false)
          }}
        />
      )}
      {isCopied && <IdIsCopied idCopied={!!isCopied} value={isCopied} />}
      {confirmDelete && template && (
        <Confirm
          title={t('DELETE_NOTIFICATION')}
          visible={confirmDelete}
          onClose={handleDeleteConfirm}
          message={`Are you sure to delete "${template.name} - ${(template.language || 'en').toUpperCase()}" template?`}
        />
      )}
    </div>
  )
}
